<template>
  <Modal
    :width="'100vw'"
    :visible="props.visible"
    @update:visible="(v) => (v ? emits('update:visible', v) : close())"
    :footer="null"
    :title="null"
    :maskClosable="false"
    :wrapClassName="$style.Modal"
  >
    <Header
      :class="$style.Header"
      style="padding: 16px"
      :avatarUrl="orgInfo.logo"
      :title="orgInfo.fullName"
      :lingdouNum="lingdouInfo?.total"
      :isPersonal="isPersonal"
      :expireTime="lingdouInfo?.memberShip?.expireTime"
      :isVip="isVip"
      :avatarSize="48"
      v-if="orgInfo"
    />

    <div
      :class="$style.Padding"
      style="display: flex; flex-direction: column; margin-top: 16px"
      v-else
    >
      <SkeletonButton :class="[$style.SkeletonButton]" style="width: 100px; height: 72px" />
      <SkeletonButton
        :class="[$style.SkeletonButton]"
        style="width: 100px; height: 38px; margin-top: 16px"
      />
    </div>

    <div :class="$style.SkuError" v-if="skuError"
      ><Button type="primary" @click="init">出错啦，点击重新加载</Button></div
    >
    <div :class="[$style.Skus, $style.Padding]" v-else-if="skuLoading">
      <SkeletonButton v-for="item in 2" :key="item" :class="$style.SkeletonButton" />
    </div>

    <template v-else>
      <Tab
        v-if="!props.onlyLingdou"
        :active="activeTab"
        @update:active="handleUpdateTab"
        :tabs="tabsList"
      />

      <div
        v-if="!isPersonal && activeTab === SKU_TYPE.VIP"
        :class="$style.PayBlock"
        style="margin-top: 16px; padding: 8px 16px"
      >
        <SelectPeople
          :value="selectPeople"
          @update:value="handleUpdateSelectPeople"
          :showTip="selectPeople && isVip"
          :expireTime="lingdouInfo?.memberShip?.expireTime"
      /></div>

      <div :class="[$style.Skus]">
        <Sku
          v-for="item in skusList"
          :key="item.id"
          :description="item.description"
          :des="item.des"
          :amount="item.amount"
          :originPrice="item.originPrice"
          :price="item.price"
          :expiresIn="item.expiresIn"
          :active="item.skuId === activeSkuId"
          :type="item.type"
          :first="item.first"
          @click="handleActiveSku(item.skuId)"
        />
      </div>
    </template>

    <ChannelSelect
      v-if="!showCustomer && !showMemberTip"
      :class="$style.Padding"
      style="margin-top: 16px"
      v-model:value="channel"
    />

    <!-- 购买超过20人 -->
    <Customer style="margin: 16px 16px 0" v-if="showCustomer" />

    <div
      v-else-if="showMemberTip"
      :class="$style.Padding"
      style="margin-top: 16px; background-color: rgba(255, 255, 255, 0.04); padding: 16px"
    >
      <!-- 购买的席位小于当前席位 -->
      <div :class="$style.MemberTip" v-if="showMemberTip">
        <template v-if="isAdmin">
          您当前团队内人数超出该套餐团队人数上限，请使用电脑端管理团队人数或更换其他套餐。
          <div style="text-align: center"
            ><Button :class="$style.Copy" type="primary" @click="handleCopy">{{
              isCopy ? '复制成功' : '复制网站地址'
            }}</Button></div
          ></template
        >
        <template v-else>
          您当前团队内人数超出该套餐团队人数上限，请联系管理员管理团队人数或更换其他套餐。
        </template>
      </div>
    </div>

    <div :class="$style.Padding" style="margin-top: 16px">
      <Introduction :skuType="activeTab" :seat="selectPeople" :isPersonal="isPersonal"
    /></div>

    <div :class="$style.Footer" v-if="!showCustomer && !showMemberTip">
      <div :class="[$style.Protocol]"
        ><span
          ><Checkbox :class="$style.CheckBox" v-model:checked="agree" />同意<Protocol
            :isActiveVipTab="activeTab === SKU_TYPE.VIP" /></span
      ></div>

      <MobilePay
        :key="refreshPayComponent"
        :agree="agree"
        :skuId="activeSkuId"
        :channel="channel"
        :wxConfig="props.wxConfig"
        :zfbConfig="props.zfbConfig"
        :isActiveVipTab="activeTab === SKU_TYPE.VIP"
        @success="handlePaySuccess"
      />
    </div>
  </Modal>
</template>

<script lang="ts" setup>
  import { ref, watch, computed } from 'vue';
  import { Modal, SkeletonButton, Button, Checkbox } from 'ant-design-vue';
  import Header from '../components/header.vue';
  import Sku from '../components/sku.vue';
  import ChannelSelect from '../components/channel-select.vue';
  import Protocol from '../components/protocol.vue';
  import MobilePay from '../components/mobile-pay.vue';
  import Tab from '../components/tab/index.vue';
  import Introduction from '../components/introduction/index.vue';

  import { GetSkusRes } from '../apis/entities';
  import { usePayment } from './hooks/usePayment';
  import { PAY_CHANNEL, SKU_TYPE } from '../constants';
  import { useTab } from './hooks/useTab';
  import SelectPeople from '../components/select-people.vue';
  import Customer from '../components/customer.vue';
  import { copyTextToClipboard } from '../utils/copy';

  const props = defineProps({
    visible: {
      type: Boolean,
      default: false,
    },

    getUserInfo: {
      type: Function,
      default: null,
    },

    getOrgInfo: {
      type: Function,
      default: null,
    },

    getLingdouInfo: {
      type: Function,
      default: null,
    },

    onPaySuccess: {
      type: Function,
      default: null,
    },

    onPayCancel: {
      type: Function,
      default: null,
    },

    defaultActiveSkuId: {
      type: Number,
      default: null,
    },

    defaultActiveTab: {
      type: String,
      default: null,
    },

    // 只展示灵豆包
    onlyLingdou: {
      type: Boolean,
      default: false,
    },

    wxConfig: {
      type: Object,
      required: true,
    },

    zfbConfig: {
      type: Object,
      required: true,
    },
  });

  const emits = defineEmits(['update:visible']);
  const selectPeople = ref('5');

  const {
    skus,
    skuLoading,
    skuError,
    orgInfo,
    lingdouInfo,
    activeSkuId,
    init,
    handleActiveSku,
    isPersonal,
    isAdmin,
    isVip,
  } = usePayment({
    getUserInfo: props.getUserInfo,
    getOrgInfo: props.getOrgInfo,
    getLingdouInfo: props.getLingdouInfo,
  });

  const { activeTab, tabs, handleActiveTab } = useTab(isPersonal);

  const showMemberTip = computed(
    () =>
      !skuLoading.value &&
      activeTab.value === SKU_TYPE.VIP &&
      orgInfo.value?.memberCount > +selectPeople.value,
  );

  const showCustomer = computed(
    () => !skuLoading.value && activeTab.value === SKU_TYPE.VIP && !skusList.value.length,
  );

  const channel = ref(PAY_CHANNEL.WECHAT);
  const agree = ref(false);

  const tabsList = computed(() => {
    return tabs.value
      .filter((tab) => {
        if (props.onlyLingdou) {
          return tab.value === SKU_TYPE.PURCHASE;
        }
        return true;
      })
      .map((tab) => {
        if (tab.value === SKU_TYPE.VIP) {
          return {
            ...tab,
            first: !!skus.value.find((v: GetSkusRes) => v.type === SKU_TYPE.VIP && v.first),
          };
        } else {
          return {
            ...tab,
            first: !!skus.value.find((v: GetSkusRes) => v.type === SKU_TYPE.PURCHASE && v.first),
          };
        }
      });
  });

  const skusList = computed(() => {
    // 展示vip商品
    if (activeTab.value === SKU_TYPE.VIP) {
      return skus.value.filter(
        (v: GetSkusRes) =>
          v.type === SKU_TYPE.VIP && (isPersonal.value ? true : v.seat === selectPeople.value),
      );
    }

    // 展示单购商品
    if (activeTab.value === SKU_TYPE.PURCHASE) {
      return skus.value.filter((v: GetSkusRes) => v.type === SKU_TYPE.PURCHASE);
    }

    return [];
  });

  watch(
    () => props.visible,
    async (v) => {
      if (v) {
        await init();

        // 如果有默认选中的sku，则切换对应tab下
        if (props.defaultActiveSkuId) {
          const sku = skus.value.find((v) => v.skuId === props.defaultActiveSkuId);
          if (sku) {
            handleActiveSku(props.defaultActiveSkuId);
            activeTab.value = sku.type;
            selectPeople.value = sku.seat;
          }
        } else {
          if (orgInfo.value.memberCount > 20) {
            selectPeople.value = '';
          } else {
            const breakPoint = [5, 10, 20];
            for (let i = 0; i < breakPoint.length; i++) {
              if (orgInfo.value.memberCount <= breakPoint[i]) {
                selectPeople.value = breakPoint[i] + '';
                break;
              }
            }
          }

          if (props.onlyLingdou) {
            handleUpdateTab(SKU_TYPE.PURCHASE);
          } else {
            // 有默认选中的tab，切换并激活当前tab下第一个sku
            if (props.defaultActiveTab) {
              handleUpdateTab(props.defaultActiveTab);
            }
            // 选中
            else {
              handleUpdateTab(SKU_TYPE.VIP);
            }
          }
        }
      }
    },
    {
      immediate: true,
    },
  );

  // 选择团队人数
  function handleUpdateSelectPeople(v) {
    selectPeople.value = v;

    if (skusList.value[0]) {
      // 默认激活第一个sku
      handleActiveSku(skusList.value[0].skuId);
    }
  }

  // 切换tab
  function handleUpdateTab(v: SKU_TYPE) {
    handleActiveTab(v);

    if (skusList.value[0]) {
      // 默认激活第一个sku
      handleActiveSku(skusList.value[0].skuId);
    }
  }

  const refreshPayComponent = ref(0);
  watch(
    () => activeSkuId.value,
    () => {
      refreshPayComponent.value++;
    },
  );

  // 支付成功
  function handlePaySuccess(result) {
    close();
    props?.onPaySuccess(result);
  }

  function close() {
    props?.onPayCancel();
    emits('update:visible', false);
  }

  const isCopy = ref(false);
  function handleCopy() {
    copyTextToClipboard(location.href);
    isCopy.value = true;
  }
</script>

<style lang="less" module>
  .Modal {
    padding: 0;

    :global {
      .ant-modal {
        position: fixed;
        max-width: unset;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
        margin: 0;
        padding: 0 !important;
      }

      .ant-modal-content {
        height: 100%;
        box-shadow: unset !important;
      }

      .ant-modal-body {
        height: 100%;
        overflow: auto;
        padding: 0 0 100px;
      }
    }
  }

  .Padding {
    margin: 0 16px;
  }

  .Header {
    background: url('https://static.lingvisions.com/public/1f57e0fba7d1455295a2009e2a827b11.png')
      center / cover no-repeat;
  }

  .SkeletonButton {
    width: 165px !important;
    height: 120px;

    > span {
      width: 100% !important;
      height: 100% !important;
    }
  }

  .Tip {
    display: flex;
    justify-content: space-between;

    > div {
      color: rgba(255, 255, 255, 0.85);
      font-size: 12px;
      font-weight: 400;
      line-height: 20px;

      > span {
        color: rgba(209, 108, 68, 1);
      }
    }

    > a {
      font-size: 12px;
      font-weight: 400;
      line-height: 20px;
      color: rgba(255, 255, 255, 0.85) !important;

      &:hover {
        color: rgba(209, 108, 68, 1) !important;
      }
    }
  }

  .CheckBox {
    margin-right: 8px !important;

    :global {
      .ant-checkbox-inner {
        background-color: rgba(31, 31, 31, 1) !important;
        border-color: rgba(66, 66, 66, 1) !important;
      }
    }
  }

  .Skus {
    display: grid;
    justify-content: center;
    grid-template-columns: repeat(2, 180px);
    gap: 10px 16px;
    margin-top: 16px;
    max-height: 260px;
    overflow: auto;
  }

  .PayBlock {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: rgba(40, 40, 40, 1);
    padding: 0 16px;
    border-radius: 6px;
  }

  .Protocol {
    margin-top: 16px;
    padding-left: 16px;
    background-color: rgba(48, 48, 48, 1);
    height: 36px;
    display: flex;
    align-items: center;

    > span {
      display: flex;
      align-items: center;
    }
  }

  .SkuLoading {
    display: flex;
    align-items: center;
  }

  .SkuError {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 190px;
  }

  .Footer {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    background: rgb(31, 31, 31);
  }

  .Question {
    font-size: 12px;
    font-weight: 400;
    line-height: 20px;
    text-align: center;

    > a {
      color: rgba(255, 255, 255, 0.85) !important;
    }
  }

  .Copy {
    width: 176px;
    height: 40px !important;
    margin-top: 8px;
  }

  @media (max-width: 500px) {
    .Skus {
      grid-template-columns: repeat(2, 165px);
    }
  }
</style>
